import { ChevronDown, ChevronRight } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { EmptyValue } from '@/components/common/EmptyValue';
import { actionLabel, idsInUnit, type MatrixUnit } from '../types/permissionMatrix';

function roleLabel(names: string[]): string {
  const [first, ...rest] = names;
  return rest.length === 0 ? first! : `${first} +${rest.length}`;
}

interface PermissionMatrixUnitProps {
  unit: MatrixUnit;
  index: number;
  isCollapsed: boolean;
  isLocked: boolean;
  selectedIds: ReadonlySet<number>;
  coveredBy: ReadonlyMap<number, string[]>;
  onToggleUnit: () => void;
  onToggle: (id: number) => void;
  onGrant: (action: string | undefined, shouldGrant: boolean) => void;
}

export function PermissionMatrixUnit({
  unit,
  index,
  isCollapsed,
  isLocked,
  selectedIds,
  coveredBy,
  onToggleUnit,
  onToggle,
  onGrant,
}: PermissionMatrixUnitProps) {
  const idsFor = (action?: string) => idsInUnit(unit, action, coveredBy);

  const holdsAll = (action?: string) => {
    const ids = idsFor(action);
    return ids.length > 0 && ids.every((id) => selectedIds.has(id));
  };

  return (
    <section className="overflow-hidden rounded-2xl bg-card shadow-sm ring-1 ring-primary/12">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b bg-muted/30 px-4 py-2.5">
        <button
          type="button"
          onClick={onToggleUnit}
          className="flex items-center gap-2 text-sm font-semibold"
          aria-expanded={!isCollapsed}
        >
          {isCollapsed ? <ChevronRight className="size-4" /> : <ChevronDown className="size-4" />}
          <span className="tabular-nums text-muted-foreground">{index}</span>
          {unit.unit}
          <span className="font-normal text-muted-foreground">({unit.rows.length})</span>
        </button>

        {isLocked ? null : (
          <div className="ml-auto flex flex-wrap items-center gap-1 text-xs">
            <span className="text-muted-foreground">whole unit:</span>
            {[undefined, ...unit.actions].map((action) => {
              const granted = holdsAll(action);
              return (
                <Button
                  key={action ?? 'all'}
                  type="button"
                  variant={granted ? 'secondary' : 'ghost'}
                  size="sm"
                  aria-pressed={granted}
                  onClick={() => onGrant(action, !granted)}
                >
                  {action === undefined ? 'everything' : `${actionLabel(action)} all`}
                </Button>
              );
            })}
          </div>
        )}
      </div>

      {isCollapsed ? null : (
        <div className="overflow-x-auto">
          <table className="w-full table-fixed text-sm">
            <colgroup>
              <col className="w-14" />
              <col className="w-80" />
              {unit.actions.map((action) => (
                <col key={action} />
              ))}
            </colgroup>
            <thead>
              <tr className="border-b text-xs text-muted-foreground">
                <th className="px-3 py-2 text-left font-medium">SL</th>
                <th className="px-3 py-2 text-left font-medium">Menu / page</th>
                {unit.actions.map((action) => (
                  <th key={action} className="px-3 py-2 text-center font-medium uppercase">
                    {actionLabel(action)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {unit.rows.map((row, rowIndex) => (
                <tr key={row.screen} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="px-3 py-2 text-muted-foreground tabular-nums">{rowIndex + 1}</td>
                  <td className="px-3 py-2">
                    <span className="block">{row.label}</span>
                    <span className="block font-mono text-xs text-muted-foreground">
                      {row.screen}
                    </span>
                  </td>
                  {unit.actions.map((action) => {
                    const permission = row.byAction.get(action);
                    return (
                      <td key={action} className="px-3 py-2 text-center">
                        {permission === undefined ? (
                          <EmptyValue />
                        ) : coveredBy.has(permission.id) ? (
                          <Badge
                            variant="secondary"
                            className="max-w-full"
                            title={`Comes from ${coveredBy.get(permission.id)!.join(', ')}`}
                          >
                            {roleLabel(coveredBy.get(permission.id)!)}
                          </Badge>
                        ) : (
                          <Checkbox
                            className="mx-auto block"
                            checked={selectedIds.has(permission.id)}
                            onCheckedChange={() => onToggle(permission.id)}
                            disabled={isLocked}
                            aria-label={`${actionLabel(action)} on ${row.label}`}
                          />
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
